<template>
  <UIDropdown
    v-bind="$attrs"
    :class="['GptContextMenu', $attrs.class]"
    :items="dropdownOptions"
    placement="bottom-end"
    @click.capture.prevent=""
  >
    <template #toggle-container="{ isOpen }">
      <UIButton
        data-testid="gpt-card-menu"
        :class="[
          'GptContextMenu__menu',
          { 'GptContextMenu__menu--is-visible': isOpen || isMenuVisible },
        ]"
        leadIcon="dotsHorizontal"
        aria-label="More options"
        theme="tertiary"
        size="sm"
        @click="publicLink.onMenuToggle()"
      />
    </template>

    <!-- Same row as ui-lib's default, plus the test id the e2e specs target. -->
    <template #option="{ option }">
      <UIIcon
        v-if="option.icon"
        :key="option.icon"
        class="GptContextMenu__option-icon"
        :name="option.icon"
      />
      <span :data-testid="`gpt-menu-${optionTestId(option.label)}`">
        {{ option.label }}
      </span>
    </template>
  </UIDropdown>

  <ModalDeleteGpt
    :isOpen="deleteGptModal.isOpen"
    :gpt="props.gpt"
    :isLoading="isDeleting"
    @close="deleteGptModal.close()"
    @confirm="submitDelete()"
  />

  <ModalGptShare
    :isOpen="shareGptModal.isOpen"
    :isLoading="update.isPending.value"
    :gpt="props.gpt"
    @close="closeShareModal()"
    @confirm="submitShare"
  />

  <ModalGptPublicLink
    v-if="isPlarformAdmin"
    :isOpen="publicLink.modal.isOpen"
    :gpt="props.gpt"
    @close="publicLink.modal.close()"
  />

  <ModalGptRating
    :isOpen="ratingModal.isOpen"
    :isLoading="isLoadingRating"
    @close="ratingModal.close()"
    @confirm="submitRating"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { UIButton, UIDropdown, UIIcon, type IIcons } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { handleApiErrorToast } from '@/apiClient'
import { useGptQueries } from '@/apiClient/queries/useGptQueries'
import type { IGpt, IGptListItem, IGptPermissions } from '@/apiClient/types/gpt'
import { useGptPublicLinkAction } from '@/composables/useGptPublicLinkAction'
import { useModal } from '@/composables/useModal'
import { useAuthStore } from '@/store/auth'
import { useGptStore } from '@/store/gpt'
import { useToast } from '@/store/toast'
import ModalDeleteGpt from '@/views/components/modals/ModalDeleteGpt.vue'
import ModalGptPublicLink from '@/views/components/modals/ModalGptPublicLink.vue'
import ModalGptRating from '@/views/components/modals/ModalGptRating.vue'
import ModalGptShare from '@/views/components/modals/ModalGptShare.vue'
import type { IDropdownItem } from '@/views/components/ui/dropdowns/types'

defineOptions({
  inheritAttrs: false,
})
const emit = defineEmits(['refresh'])

const props = defineProps<{ gpt: IGptListItem; isMenuVisible?: boolean }>()

const optionTestId = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const { isPlarformAdmin } = storeToRefs(useAuthStore())

const router = useRouter()
const { update, togglePin, remove, rate } = useGptQueries()

const dropdownOptions = computed<IDropdownItem[]>(() => {
  // A draft isn't finalized: only Edit (continue) and Delete (discard) apply —
  // hide chat/pin/share until it's published.
  const isDraft = !!props.gpt.is_draft
  return [
    ...(!isDraft
      ? [
          {
            label: 'New chat',
            value: 'new-chat',
            onClick: () => {
              router.push({ name: 'NewChat', query: { gptUuid: props.gpt.id } })
            },
            icon: 'penToSquareRegular' as const,
          },
        ]
      : []),
    ...(props.gpt.is_editable
      ? [
          {
            label: 'Edit',
            value: 'edit',
            onClick: () => {
              router.push({ name: 'GptUpdate', params: { uuid: props.gpt.id } })
            },
            icon: 'pencil' as const,
          },
        ]
      : []),
    ...(!isDraft
      ? [
          {
            label: props.gpt.is_pinned
              ? 'Hide from sidebar'
              : 'Keep in sidebar',
            value: 'pin',
            onClick: () => {
              togglePin
                .mutateAsync(props.gpt.id)
                .then(({ pinned }) => {
                  useToast().onSuccess(
                    `GPT was successfully ${
                      pinned ? 'pinned to' : 'hidden from'
                    } the sidebar`,
                  )
                })
                .catch(handleApiErrorToast)
            },
            icon: (props.gpt.is_pinned
              ? 'eyeSlashRegular'
              : 'thumbtackRegular') as IIcons,
          },
        ]
      : []),
    ...(props.gpt.is_editable && !isDraft
      ? [
          {
            label: 'Share',
            value: 'share',
            onClick: () => {
              shareGptModal.open()
            },
            icon: 'userGroupSolid' as const,
          },
        ]
      : []),
    ...(isPlarformAdmin.value && !isDraft
      ? [
          {
            label: publicLink.label.value,
            value: 'public-link',
            onClick: () => {
              publicLink.modal.open()
            },
            icon: 'globe' as const,
          },
        ]
      : []),
    ...(props.gpt.is_ratable
      ? [
          {
            label: 'Rate GPT',
            value: 'rate',
            onClick: () => {
              ratingModal.open()
            },
            icon: 'starRegular' as const,
          },
        ]
      : []),
    ...(props.gpt.is_editable
      ? [
          {
            label: 'Delete',
            value: 'delete',
            onClick: () => {
              deleteGptModal.open()
            },
            icon: 'trashRegular' as const,
            isDestructive: true,
          },
        ]
      : []),
  ]
})

const deleteGptModal = useModal<IGpt>()
const isDeleting = ref(false)
const submitDelete = () => {
  if (isDeleting.value) return

  isDeleting.value = true
  remove
    .mutateAsync(props.gpt.id)
    .then(() => {
      useToast().onSuccess('Your GPT was deleted successfully.')
      emit('refresh')
    })
    .finally(() => {
      deleteGptModal.close()
      isDeleting.value = false
    })
}

const shareGptModal = useModal<IGpt>()
const publicLink = useGptPublicLinkAction(
  computed(() => props.gpt.id),
  isPlarformAdmin,
)
// Used to track the share API call
const submitShare = (payload: {
  is_public: boolean
  permissions: IGptPermissions[]
}) => {
  update.mutateAsync({
    ...props.gpt,
    pinned: useGptStore().allGpts.find((gpt) => gpt.id === props.gpt.id)
      ?.pinned,
    ...payload,
  })
}
const closeShareModal = () => {
  shareGptModal.close()
}

// Rating
const ratingModal = useModal<number>()
const isLoadingRating = ref(false)
const submitRating = (rating: number) => {
  if (isLoadingRating.value) return

  isLoadingRating.value = true
  rate
    .mutateAsync({ gptId: props.gpt.id, rating: rating })
    .then(() => {
      useToast().onSuccess('GPT rating was submitted successfully.')
      ratingModal.close()
      emit('refresh')
      useGptStore().refreshGpt(props.gpt.id)
    })
    .catch(handleApiErrorToast)
    .finally(() => {
      isLoadingRating.value = false
    })
}
</script>

<style scoped lang="scss">
.GptContextMenu {
  .GptContextMenu__menu {
    // Glyph size is set by the host (sidebar/topbar use 1rem via
    // `--gpt-context-menu-icon-size`); GPT cards default to the original 1.5rem.
    // UIButton ignores a size set on its root, so target its icon directly.
    --gpt-context-menu-glyph: var(--gpt-context-menu-icon-size, 1.5rem);
    min-width: calc(var(--gpt-context-menu-glyph) + 0.5rem);
    min-height: calc(var(--gpt-context-menu-glyph) + 0.5rem);
    display: none;

    :deep(.UIButton__icon) {
      --size: var(--gpt-context-menu-glyph);
    }

    &--is-visible {
      display: inline-flex;
    }
  }
}

.GptContextMenu__option-icon {
  --size: 1rem;
  --color: var(--icon-color);
}
</style>
